import {Trans} from '@lingui/macro';
import _, {isEqual} from 'lodash';
import React, {FC, ReactElement} from 'react';
import styled, {css} from 'styled-components';

import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {MultipleGroupsDropdownBoxProps} from './multipleGroupsDropdownBox';
import {DropdownGroup} from './multipleGroupsDropdownHelpers';

const maxItemsToRender = 3;

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 5px;
`;

const StyledNameDiv = styled.div`
  ${ellipsis()};
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  ${ellipsis()};
`;

const StyledMeta = styled.span<{$noPaddingRight?: boolean}>`
  color: ${(p) => p.theme.greys.shade60};
  padding: 0 5px;
  ${(p) =>
    p.$noPaddingRight &&
    css`
      padding-right: 0;
    `}
`;

/*
 * Component.
 */

export const MultipleGroupsDropdownValue = <T,>(props: MultipleGroupsDropdownBoxProps<T>): ReturnType<FC> => {
  // Show placeholder if we don't have anything selected, which can be if selectedItems is null or empty array while selecting `any` item is disabled
  if (props.selectedItems === null || (!props.canSelectAnyItem && props.selectedItems.length === 0)) {
    return (
      <StyledValueDiv>
        <StyledPlaceholderDiv>{props.placeholder}</StyledPlaceholderDiv>
      </StyledValueDiv>
    );
  }

  // Show `any item` is selected when selectedItems is empty array and we have enabled selecting `any item`
  if (props.canSelectAnyItem && props.selectedItems.length === 0) {
    return (
      <StyledValueDiv>
        {props.anyIcon ? <StyledIconDiv>{props.anyIcon}</StyledIconDiv> : null}
        <StyledNameDiv>{props.anyLabel}</StyledNameDiv>
      </StyledValueDiv>
    );
  }

  const selectedItems = props.selectedItems.slice(0, props.maxItemsToRender ?? maxItemsToRender);
  const extraSelectedItems = props.selectedItems.slice(props.maxItemsToRender ?? maxItemsToRender).length;

  return (
    <StyledValueDiv>
      {renderSelectedItems<T>(props.groups, selectedItems, props.selectedItemsSeparator)}
      {extraSelectedItems ? <StyledMeta $noPaddingRight>, +{extraSelectedItems}</StyledMeta> : null}
    </StyledValueDiv>
  );
};

/*
 * Helpers.
 */

function renderSelectedItems<T>(
  groups: ReadonlyArray<DropdownGroup<T>>,
  selectedItems: ReadonlyArray<T>,
  selectedItemsSeparator: React.ReactNode | undefined,
) {
  return _(groups)
    .map((group) => {
      const allItems = [...group.items, ...(group.extraItems || [])];
      const items = allItems.filter((item) => selectedItems.find((i) => isEqual(i, item)));

      if (!items.length) {
        return null;
      }

      return items.map((item) => {
        const icon = group.renderItemIcon?.(item, true);

        return (
          <React.Fragment key={group.getItemKey(item)}>
            {icon ? <StyledIconDiv>{icon}</StyledIconDiv> : null}
            <StyledNameDiv>{group.renderItemLabel(item, true)}</StyledNameDiv>
          </React.Fragment>
        );
      });
    })
    .filter(Boolean)
    .flatten()
    .map((item: ReactElement, i: number) => {
      if (i !== 0) {
        return (
          <React.Fragment key={item.key}>
            <StyledMeta>{selectedItemsSeparator ?? <Trans id="or">or</Trans>}</StyledMeta>
            {item}
          </React.Fragment>
        );
      }

      return item;
    })
    .value();
}
